// Piezas de los bloques de promos de un producto en «Arma la venta» (teléfono y accesorios):
// grupos de color, renglón de «va sola», renglón de acumulable y títulos.
import { Gift, Tag, Ticket, Wallet, type LucideIcon } from 'lucide-react';
import {
    PromotionGroupRow,
    SelectablePromotionRow,
} from '@/domains/sales/wizard/components/PromotionGroup';
import { formatMXN } from '@/shared/lib/format/money';
import type { EvaluatedSalePromotion } from '../api/wizardPromotionsContract';
import { blockedReasonCopy } from '../lib/promotionErrorCopy';
import type { BenefitGroup, PromotionPiece } from '../lib/skuPromotionPieces';
import { PaymentOptionsChip } from './PaymentOptionsChip';

export const GROUPS: readonly { group: BenefitGroup; label: string; icon: LucideIcon }[] = [
    { group: 'gift', label: 'Regalo', icon: Gift },
    { group: 'discount', label: 'Descuento', icon: Tag },
    { group: 'benefit', label: 'Beneficio', icon: Wallet },
    { group: 'coupon', label: 'Cupón', icon: Ticket },
];

/** «Galaxy −$300.00 · Cargador −$52.50»: lo que baja cada línea con la promo. */
export function lineBreakdown(
    promo: EvaluatedSalePromotion,
    names: ReadonlyMap<string, string>,
): string | null {
    const parts = promo.lines
        .filter((l) => l.discountCents > 0)
        .map((l) => `${names.get(l.lineId) ?? 'Línea'} −${formatMXN(l.discountCents / 100)}`);
    return parts.length > 0 ? parts.join(' · ') : null;
}

/** Qué ahorra: el desglose por línea si está elegida; si no, el total. */
function savingsText(
    promo: EvaluatedSalePromotion,
    chosen: boolean,
    names: ReadonlyMap<string, string>,
): string | null {
    if (promo.blockedBy) return blockedReasonCopy(promo.blockedBy);
    if (chosen) return lineBreakdown(promo, names);
    return promo.totalDiscountCents > 0
        ? `Ahorras ${formatMXN(promo.totalDiscountCents / 100)}`
        : null;
}

/** Fila elegible con el look del detalle del catálogo: radio, nombre, qué da y el resto. */
export function SoloPromotionRow({
    piece,
    promo,
    icon,
    checked,
    disabled,
    names,
    onSelect,
    pending = false,
}: {
    piece: PromotionPiece;
    promo: EvaluatedSalePromotion;
    icon: LucideIcon;
    checked: boolean;
    disabled: boolean;
    names: ReadonlyMap<string, string>;
    onSelect: () => void;
    /** Elegida, a la espera de su regalo: aún no está apartada. */
    pending?: boolean;
}) {
    const savings = savingsText(promo, checked, names);
    const description = [
        ...(piece.gift && piece.gift.options.length > 0
            ? [piece.gift.options.map((o) => o.name).join(' o ')]
            : []),
        ...piece.texts,
        ...(piece.extras !== null ? [piece.extras] : []),
    ].join(' · ');
    const meta = pending
        ? 'Falta elegir el regalo'
        : [savings, piece.conditions].filter(Boolean).join(' · ');
    return (
        <SelectablePromotionRow
            role="radio"
            checked={checked}
            disabled={disabled}
            pending={pending}
            onSelect={onSelect}
            tone={piece.group}
        >
            <PromotionGroupRow
                icon={icon}
                tone={piece.group}
                name={piece.name}
                aside={piece.paymentLabel && <PaymentOptionsChip label={piece.paymentLabel} />}
                description={description}
                meta={meta}
                metaTone={pending ? 'warning' : 'muted'}
            />
        </SelectablePromotionRow>
    );
}

/** Renglón de una acumulable: casilla con el color de su grupo. */
export function StackableChoice({
    piece,
    icon,
    label,
    checked,
    disabled,
    description,
    onToggle,
}: {
    piece: PromotionPiece;
    icon: LucideIcon;
    label?: string;
    checked: boolean;
    disabled: boolean;
    description: string;
    onToggle: () => void;
}) {
    return (
        <SelectablePromotionRow
            role="checkbox"
            tone={piece.group}
            checked={checked}
            disabled={disabled}
            onSelect={onToggle}
        >
            <PromotionGroupRow
                icon={icon}
                tone={piece.group}
                name={label ?? piece.name}
                aside={piece.paymentLabel && <PaymentOptionsChip label={piece.paymentLabel} />}
                description={description}
                meta={piece.conditions ?? undefined}
            />
        </SelectablePromotionRow>
    );
}

/** Encabezado de un bloque: «Va sola · elige una» / «Se suman · marca las que quieras». */
export function SectionTitle({ title, hint }: { title: string; hint: string }) {
    return (
        <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {title} <span className="font-normal normal-case tracking-normal">· {hint}</span>
        </div>
    );
}
